import * as React from "react";
import {withRouter} from "react-router-dom";
import {RouteComponentProps} from "react-router";
import Loading from "../ui/loading/Loading";
import {none, option, Option} from "ts-option";
import {connect, Dispatch} from "react-redux";
import {RootState} from "../../store/reducer/root/RootState";
import {Icon} from "../../hiberUiKit/src/atoms/Icon/Icon";
import DataGrid from "../../hiberUiKit/src/organisms/DataGrid/DataGrid";
import {api} from "../../api/api";
import PartnerBrand from "../../model/partnerBrand/PartnerBrand";
import DateUtils from "../../utils/DateUtils";
import Bill, {BillStatus} from "../../model/bill/Bill";
import CurrencyHelper from "../../helper/CurrencyHelper";
import {CommonDetailsStatusRow, DetailsStatusPathItem} from "./common/CommonDetailsStatusRow";
import {default as CommonDetailsInfoRow, DetailsInfoRowItem} from "./common/CommonDetailsInfoRow";
import {CommonDetailsSummary, DetailsSummaryItem} from "./common/CommonDetailsSummary";
import {getErrorMessage, notifyBugsnagAndShowToast} from "../../utils/Utils";
import Payment from "../../model/payment/Payment";
import PaymentRecommendation from "../../model/payment/PaymentRecommendation";
import Toast from "../helper/Toast";
import {OnFailure, OnSuccess, OnSuccessData} from "../../store/action/actionHelpers";
import {PaymentsSummary} from "./common/PaymentsSummary";
import {PaymentConfirmation} from "./common/PaymentConfirmation";
import {FullPaymentConfirmation} from "./common/FullPaymentConfirmation";
import BillActivityStream from "./billActivityStream/BillActivityStream";
import AccountingNote from "../../model/invoice/note/AccountingNote";
import {BillEventType} from "../../model/bill/event/BillEvent";
import BillActivityTabs from "./billActivityTabs/BillActivityTabs";
import ShowBillInMobileSwitch from "./common/ShowBillInMobileSwitch";
import {fetchBill, updateBill} from "../../store/action/bill/bill";
import Routes from "../../navigation/route/Route";
import {
	ButtonDropdownItem,
	ButtonVariant,
	default as ButtonWithDropdown
} from "../../hiberUiKit/src/atoms/Buttons/ButtonWithDropdown";
import ConfirmSendBillModal from "./ConfirmSendBillModal";
import IconLarge from "../../hiberUiKit/src/atoms/Icon/IconLarge";
import {getJobByIdOrRefNumber} from "../../store/view/job";
import {getJobStatus} from "../../store/view/jobStatus";
import {fetchJob} from "../../store/action/job/job";
import {fetchPartnerBrand} from "../../store/action/partnerBrand/partnerBrand";
import "./billDetails.scss";
import {NotificationTypeEnum} from "../../model/notification/NotificationBase";
import BillPaidNotification from "../../model/notification/invoice/BillPaidNotification";
import TextUtils from "../../utils/TextUtils";
import FormGroup from "../../hiberUiKit/src/molecules/FormGroup/FormGroup";
import {Moment} from "moment";
import DatePickerWithIcon from "../../hiberUiKit/src/molecules/DatePickerWithIcon/DatePickerWithIcon";
import ButtonPrimary from "../../hiberUiKit/src/atoms/Buttons/ButtonPrimary";
import OptionUtils from "../../utils/OptionUtils";
import {noop} from "react-select/lib/utils";
import Job from "../../model/job/Job";
import _ = require("underscore");

interface RouteProps {
	/**
	 * Bill identifier.
	 */
	billId: string;
}

interface ReduxProps {
	/**
	 * Partner brands data.
	 */
	partnerBrand: Option<PartnerBrand>;
	/**
	 * Bill data.
	 */
	bill: Option<Bill>;
	/**
	 * Related job internal status label to be displayed.
	 */
	jobStatusLabel: Option<string>;
	/**
	 * Last bill paid notification id
	 */
	lastBillPaidNotificationId: Option<string>
	/**
	 * Bills parent job ref number
	 */
	parentJobRefNumber: Option<string>
	/**
	 * Job
	 */
	job: Option<Job>
}

interface DispatchProps {
	/**
	 * Fetch bill action.
	 */
	fetchBill: (billId: string, onSuccess: OnSuccessData<Bill>, onFailure: OnFailure) => void;
	/**
	 * Fetch job action.
	 */
	fetchJob: (jobId: string, onFailure: OnFailure) => void;
	/**
	 * Fetch job action.
	 */
	fetchPartnerBrand: (pbId: string, onFailure: OnFailure) => void;
	/**
	 * Update bill
	 * @param bill
	 * @param onSuccess
	 * @param onFailure
	 */
	updateBill: (bill: Bill, onSuccess: OnSuccess, onFailure: OnFailure) => void
}

interface State {
	/**
	 * Bill payments.
	 */
	payments: Payment[];
	/**
	 * Payment recommendations.
	 */
	paymentRecommendations: PaymentRecommendation[];
	/**
	 * Bill activity events.
	 */
	events: BillEventType[];
	/**
	 * Bill notes.
	 */
	notes: AccountingNote[];
	/**
	 * Note selected for edition.
	 */
	noteToEdit: Option<AccountingNote>;
	/**
	 * Send bill confirmation modal visibility indicator.
	 */
	confirmationModalVisible: boolean;
	/**
	 * Indicating if request to server is in progress.
	 */
	isLoading: boolean;
	/**
	 * Edit bill info
	 */
	editBillInfo: boolean
	/**
	 * Bill date
	 */
	billDate: Option<Moment>
	/**
	 * Due date
	 */
	dueDate: Option<Moment>
	/**
	 * Is loading
	 */
	isUpdateBillDatesLoading: boolean
	/**
	 * Xero bill status
	 */
	xeroBillStatus: Option<string>
}

type Props = ReduxProps & DispatchProps & RouteComponentProps<RouteProps>;

/**
 * Bill details view.
 */
class BillDetailsView extends React.PureComponent <Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {
			payments: [],
			paymentRecommendations: [],
			events: [],
			notes: [],
			noteToEdit: none,
			isLoading: false,
			confirmationModalVisible: false,
			editBillInfo: false,
			billDate: none,
			dueDate: none,
			isUpdateBillDatesLoading: false,
			xeroBillStatus: none
		};
	}

	componentDidMount(): void {
		this.fetchData(() => {}, true);
	}

	componentWillReceiveProps(nextProps: Readonly<Props>) {
		if (this.props.lastBillPaidNotificationId.orNull !== nextProps.lastBillPaidNotificationId.orNull) {
			this.fetchData(() => {});
		}
	}

	render() {
		return (
			<div className={"l-page"}>
				<Loading isLoading={this.props.bill.isEmpty}>
					{this.renderStatusRow()}
					{this.renderDetailsInfoRow()}
					<div className={"c-common-info_row-inner-container c-bill-details_bill-dates"}>
						<FormGroup label={"Bill date"}>
							{this.state.editBillInfo ?
								<DatePickerWithIcon onDateChange={this.onBillDateChange}
													initialDate={this.state.billDate.orNull}
													dateFormat={DateUtils.ISO_DATE_FORMAT}
													maxDate={this.state.dueDate.orNull}/>
								: this.props.bill.map(b => b.billDate.format(DateUtils.ISO_DATE_FORMAT)).getOrElse("N/A")}
						</FormGroup>
						<FormGroup label={"Due date"}>
							{this.state.editBillInfo && this.props.bill.map(b => b.payViaMobile).getOrElse(false) ?
								<DatePickerWithIcon onDateChange={this.onBillDueDateChange}
													initialDate={this.state.dueDate.orNull}
													dateFormat={DateUtils.ISO_DATE_FORMAT}
													minDate={this.state.billDate.orNull}
													clearable={this.props.bill.map(b => !b.payViaMobile).getOrElse(true)}/>
								: this.props.bill.flatMap(b => b.dueDate.map(d => d.format(DateUtils.ISO_DATE_FORMAT))).getOrElse(
									"N/A")}
						</FormGroup>
					</div>
					{this.renderTable()}
					<div className="c-common-details_common-summary_external-container">
						{this.renderSummary()}
					</div>
					{this.state.paymentRecommendations.map(this.renderRecommendationConfirmation)}
					{this.props.bill.map(b => {
						return b.status === BillStatus.PAID_NEEDS_CONFIRMATION &&
							<FullPaymentConfirmation payment={this.state.payments[0]}
													 confirmFullPayment={_.throttle(() => this.confirmFullPayment(b.id), 300)}
													 isLoading={this.state.isLoading}
													 renderBodyMessage={this.renderFullPaymentItemBodyMessage}/>
					}).orNull}
					<div className="c-common-details_activity-tabs-container">
						<BillActivityTabs bill={this.props.bill}
										  noteToEdit={this.state.noteToEdit}
										  onAddNoteSuccess={this.onAddNoteSuccess}
										  onUpdateNoteSuccess={this.onUpdateNoteSuccess}/>
					</div>
					{this.props.bill.map(b =>
						<BillActivityStream billId={b.id}
											activeNoteId={this.props.location.state ?
												this.props.location.state.activeId : ""}
											events={this.state.events}
											notes={this.state.notes}
											payments={this.state.payments}
											onEditNoteClick={this.onEditNoteClick}
											onDeleteNoteSuccess={this.onDeleteNoteSuccess}/>).orNull}
					{this.state.confirmationModalVisible &&
					<ConfirmSendBillModal onClose={() => this.setState({confirmationModalVisible: false})}
										  onConfirm={this.sendBill}
										  isLoading={this.state.isLoading}/>}
				</Loading>
			</div>
		);
	}

	renderDetailsInfoRow = () => {
		const detailsInfoRowItem1: DetailsInfoRowItem = {
			label: "Reference number",
			value: this.props.bill.map(b => b.referenceNumber).orNull
		};
		const detailsInfoRowItem2: DetailsInfoRowItem = {
			label: "Created",
			value: this.props.bill.map(b => b.creationDateTime.format(DateUtils.DATE_TIME_FORMAT)).orNull
		};
		const detailsInfoRowItem3: DetailsInfoRowItem = {
			label: "Job status",
			value: this.props.jobStatusLabel.orNull
		};
		const detailsInfoRowItem4: DetailsInfoRowItem = {
			label: "Partner Brand",
			value: this.props.partnerBrand.map(pb => pb.name).orNull
		};
		return <CommonDetailsInfoRow
			items={[detailsInfoRowItem1, detailsInfoRowItem2, detailsInfoRowItem3, detailsInfoRowItem4]}/>
	};

	renderTable = () => {
		return (
			<DataGrid>
				<thead>
				<tr className="c-common-details_table-header-row">
					<th className="c-common-details_table-desc-cell">DESCRIPTION</th>
					<th>QTY</th>
					<th className="c-bill-details_table-price-cell_header">UNIT PRICE</th>
					<th>DISC %</th>
					<th>TAX RATE</th>
					<th className="c-bill-details_table-price-cell">AMOUNT</th>
				</tr>
				</thead>
				<tbody>
				{this.props.bill.map(b => b.items.map((item, index) =>
					<tr key={index} className="c-common-details_table-row">
						<td className="c-common-details_table-desc-cell">{item.description}</td>
						<td>{item.quantity}</td>
						<td className="c-bill-details_table-price-cell">
							{CurrencyHelper.toCurrency(item.unitPrice)}
						</td>
						<td>{item.discount.map(d => `${d}`).getOrElse("-")}</td>
						<td>{`${item.taxRate}%`}</td>
						<td className="c-bill-details_table-price-cell">
							{CurrencyHelper.toCurrency(item.amount)}
						</td>
					</tr>)).orNull}
				</tbody>
			</DataGrid>);
	};

	renderSummary = () => {
		const isAnyPaymentOrRecommendation = this.state.paymentRecommendations.length || this.state.payments.length;
		const summaryItem1: DetailsSummaryItem = {
			label: "Subtotal",
			value: this.props.bill.map(b => CurrencyHelper.toCurrency(b.subtotal)).orNull
		};
		const summaryItem2: DetailsSummaryItem = {
			label: "Tax",
			value: this.props.bill.flatMap(b => b.tax.map(CurrencyHelper.toCurrency)).getOrElse("-")
		};
		const lastSummaryItem: DetailsSummaryItem = {
			label: "Total",
			value: this.props.bill.map(b => CurrencyHelper.toCurrency(b.total)).orNull
		};
		return (
			<CommonDetailsSummary items={[summaryItem1, summaryItem2]}
								  totalItem={lastSummaryItem}
								  extraWidth={true}
								  style={{marginRight: 144}}>
				{!!isAnyPaymentOrRecommendation && this.renderPayments()}
			</CommonDetailsSummary>
		);
	};

	renderPayments = () => this.props.bill.map(b =>
		<PaymentsSummary totalValue={b.total}
						 payments={this.state.payments}
						 paymentRecommendations={this.state.paymentRecommendations}/>
	).orNull;

	renderRecommendationConfirmation = (recommendation: PaymentRecommendation) => {
		return (
			<PaymentConfirmation recommendation={recommendation}
								 isLoading={this.state.isLoading}
								 confirmPayment={_.throttle(() => this.confirmPayment(recommendation.id), 300)}
								 rejectPayment={_.throttle(() => this.rejectPayment(recommendation.id), 300)}/>
		)
	};

	renderStatusRow() {
		const refNumPath: DetailsStatusPathItem[] = []
		this.props.parentJobRefNumber.map(jrn => refNumPath.push({
			label: `Job ID# ${jrn}`,
			onClick: this.redirectToJob
		}))
		this.props.bill.map(b => refNumPath.push({
			label: `Bill# ${b.referenceNumber}`
		}))
		return (
			<CommonDetailsStatusRow icon={Icon.Quote}
									title={"Bill"}
									onBackClick={this.onBackClick}
									customStatusComponent={this.renderXeroStatus()}
									additionalComponent={this.renderBillActionsRow()}
									refNumPath={refNumPath}/>
		)
	}

	renderXeroStatus() {
		return <>
			<div className="c-common-status_extended-status-wrapper">
				<div className={"c-common-status_status-wrapper full-background-fill"}>
					<div className="c-common-status_status-text white">Status</div>
				</div>
				<div className={"c-common-status_status-wrapper_triangle"}/>
				<div className="c-common-status_status-wrapper full-background-fill grey">
					<div className="c-common-status_status-text dark-grey">
						{this.props.bill.map(b => b.getStatusLabel()).orNull}</div>
				</div>
			</div>
			{this.state.xeroBillStatus.map(status => <div className="c-common-status_extended-status-wrapper">
				<div className={"c-common-status_status-wrapper full-background-fill"}>
					<div className="c-common-status_status-text white">Xero Status</div>
				</div>
				<div className={"c-common-status_status-wrapper_triangle"}/>
				<div className="c-common-status_status-wrapper full-background-fill grey">
					<div className="c-common-status_status-text dark-grey">
						{status.replaceUnderscoresWithSpaces()}
					</div>
				</div>
			</div>).orNull}
		</>

	}

	renderBillActionsRow = () => {
		return this.props.bill.map(b => {
			let dropdownItems: ButtonDropdownItem[] = [];
			if (this.props.partnerBrand.map(pb => pb.email.isDefined).getOrElse(false)) {
				const sendBillButtonLabel = b.sent ? "Re-send partner's bill" : "Send partner's bill";
				dropdownItems.unshift(new ButtonDropdownItem(BillAction.SEND_BILL, sendBillButtonLabel, false, false));
			}
			dropdownItems.push(new ButtonDropdownItem(BillAction.COPY_BILL_PAYMENT_LINK, "Copy bill payment link", false, false))
			if (this.props.bill.map(b => !b.isPaid()).getOrElse(false)) {
				dropdownItems.push(new ButtonDropdownItem(BillAction.EDIT_BILL_INFO, "Edit Bill info", false, false))
				this.props.job.flatMap(j => j.data.invoicePath)
					.forEach(() => dropdownItems.push(
						new ButtonDropdownItem(BillAction.MARK_BILL_AS_PAID, "Mark Bill as Paid", false, false)))
			}
			dropdownItems.push(new ButtonDropdownItem(BillAction.DOWNLOAD_PDF, "Download Bill PDF", false, false))
			return (
				<>
					<ShowBillInMobileSwitch bill={b}/>
					{!!dropdownItems.length &&
					<div className="c-common-details_buttons_container">
						{b.sent && this.renderBillSentInfo()}
						{this.state.editBillInfo && <div className="c-common-details_buttons_common-button">
							<ButtonPrimary isLoading={this.state.isUpdateBillDatesLoading}
										   label={"Update"}
										   onClick={this.onUpdateBillDates}
										   disabled={this.isUpdateButtonDisabled()}/>
						</div>}
						<div className="c-common-details_buttons_last-button">
							<ButtonWithDropdown label={"Bill actions"}
												isLoading={this.state.isLoading}
												dropdownItems={dropdownItems}
												onDropdownItemClick={this.onActionMenuItemClick}
												buttonVariant={ButtonVariant.PRIMARY}
												displayArrowDown={false}/>
						</div>
					</div>}
				</>
			)
		}).orNull;
	};

	renderBillSentInfo() {
		return (
			<div className="c-bill-details_sent-info">
				<IconLarge icon={Icon.Email}/>
				<span>Bill sent to partner</span>
			</div>
		);
	}

	onActionMenuItemClick = (actionId: string) => {
		switch (actionId) {
			case BillAction.SEND_BILL:
				this.setState({confirmationModalVisible: true});
				break;
			case BillAction.COPY_BILL_PAYMENT_LINK:
				this.copyBillPaymentLink();
				break;
			case BillAction.EDIT_BILL_INFO:
				this.setState({
					editBillInfo: true,
					billDate: this.props.bill.map(b => b.billDate),
					dueDate: this.props.bill.flatMap(b => b.dueDate)
				})
				break;
			case BillAction.MARK_BILL_AS_PAID:
				this.markBillAsPaid()
				break;
			case BillAction.DOWNLOAD_PDF:
				this.downloadBillPdf()
				break;
		}
	}

	copyBillPaymentLink = () => {
		this.props.bill.map(bill => {
			api.instance.billApi.getPaymentLink(bill.id).then(url => {
				TextUtils.copyToClipboard(url);
				Toast.success("Bill payment link is copied successfully", "Copy to clipboard");
			}).catch(err => {
				notifyBugsnagAndShowToast(err, "Copy to clipboard");
			})
		})
	}

	markBillAsPaid = () => {
		this.props.bill.map(bill => {
			api.instance.billApi.markBillAsPaid(bill.id).then(() => {
				this.fetchData(noop, true)
				Toast.success("Bill marked as paid successfully", "Mark Bill as Paid");
			}).catch(err => {
				notifyBugsnagAndShowToast(err, "Mark Bill as Paid", getErrorMessage(err));
			})
		})
	}

	sendBill = () =>
		this.props.bill.map(bill => {
			this.setState({isLoading: true}, () =>
				api.instance.billApi.sendBillToInstaller(bill.id)
					.then(() => {
						this.setState({isLoading: false, confirmationModalVisible: false});
						Toast.success("Bill has been sent", "Bill send");
						this.fetchBill(bill.id, () => {});
					})
					.catch(err => {
						this.setState({isLoading: false});
						notifyBugsnagAndShowToast(err, "Bill send");
					})
			);
		});

	onBackClick = () => {
		if (this.props.history.length <= 1) {
			this.props.history.push(Routes.BillWorkspaceView);
		}
		else {
			this.props.history.goBack();
		}
	};

	onAddNoteSuccess = (note: AccountingNote) => this.setState({notes: this.state.notes.concat(note)});

	onUpdateNoteSuccess = (note: AccountingNote) => this.setState({
		notes: this.state.notes.filter(n => n.id !== note.id).concat(note),
		noteToEdit: none
	});

	onEditNoteClick = (note: AccountingNote) => this.setState({noteToEdit: option(note)});

	onDeleteNoteSuccess = (note: AccountingNote) => {
		this.setState({noteToEdit: none});
		this.setState({notes: this.state.notes.filter(n => n.id !== note.id)});
	};

	fetchData = (onSuccess: OnSuccess, fetchBill = true) => {
		const billId = this.props.match.params.billId;
		if (this.props.bill.isEmpty || fetchBill) {
			this.fetchBill(billId, (bill) => {
				this.fetchBillRelatedData(billId, onSuccess);
				bill.jobId.map(this.fetchJob);
				this.fetchPartnerBrand(bill.partnerBrandId)
			});
		}
		else {
			this.fetchBillRelatedData(billId, onSuccess);
			this.props.bill.get.jobId.map(this.fetchJob);
			this.fetchPartnerBrand(this.props.bill.get.partnerBrandId)
		}
	};

	fetchBillRelatedData = (billId: string, onSuccess: OnSuccess) => {
		Promise.all([
			this.fetchBillPayments(billId),
			this.fetchPaymentRecommendations(billId),
			this.fetchBillNotes(billId),
			this.fetchBillEvents(billId),
			this.fetchBillXeroStatus(billId)
		]).then(onSuccess);
	};

	fetchBill = (billId: string, onSuccess: OnSuccessData<Bill>) => {
		return this.props.fetchBill(
			billId,
			onSuccess,
			(error) => {
				notifyBugsnagAndShowToast(error, "Fetch bill");
				this.props.history.goBack();
			}
		);
	};

	fetchBillPayments = (billId: string) => {
		return api.instance.billPaymentApi.fetchBillPayments(billId).catch(error => {
			notifyBugsnagAndShowToast(error, "Fetch bill payments");
			throw error;
		}).then(payments => this.setState({payments}));
	};

	fetchPaymentRecommendations = (billId: string) => {
		return api.instance.billPaymentApi.fetchBillPaymentRecommendations(billId)
			.then(recommendations => this.setState({paymentRecommendations: recommendations}))
			.catch(err => notifyBugsnagAndShowToast(err, "Fetch payment recommendations"));
	};

	fetchBillEvents = (billId: string) => {
		return api.instance.billApi.fetchBillEvents(billId)
			.then(events => this.setState({events}))
			.catch(err => notifyBugsnagAndShowToast(err, "Fetch events"));
	};

	fetchBillNotes = (billId: string) => {
		return api.instance.noteApi.fetchBillNotes(billId)
			.then(notes => this.setState({notes}))
			.catch(err => notifyBugsnagAndShowToast(err, "Fetch notes"));
	};

	fetchBillXeroStatus = (billId: string) => {
		return api.instance.billApi.getBillXeroStatus(billId)
			.then(xeroBillStatus => this.setState({xeroBillStatus}))
			.catch(err => notifyBugsnagAndShowToast(err, "Fetch Xero Bill status"));
	};

	fetchJob = (jobId: string) => this.props.fetchJob(jobId, (error) => notifyBugsnagAndShowToast(error, "Fetch job"));
	fetchPartnerBrand = (pbId: string) => this.props.fetchPartnerBrand(pbId,
		(error) => notifyBugsnagAndShowToast(error, "Fetch partner brand"));

	confirmPayment = (recommendationId: string) => this.setState({isLoading: true},
		() => api.instance.billPaymentApi.confirmBillPaymentRecommendation(recommendationId)
			.then(() => {
				this.fetchData(() => {
					this.setState({isLoading: false});
					Toast.success("Payment confirmed successfully", "Confirm payment");
				});
			}).catch(error => {
				this.setState({isLoading: false});
				notifyBugsnagAndShowToast(error, "Confirm payment")
			})
	);

	rejectPayment = (recommendationId: string) => this.setState({isLoading: true},
		() => api.instance.billPaymentApi.rejectBillPaymentRecommendation(recommendationId)
			.then(() => {
				this.fetchData(() => {
					this.setState({isLoading: false});
					Toast.success("Payment rejected successfully", "Reject payment");
				});
			}).catch(error => {
				this.setState({isLoading: false});
				notifyBugsnagAndShowToast(error, "Reject payment")
			})
	);

	renderFullPaymentItemBodyMessage = () =>
		<div>
			Total amount for this bill has been paid. <b>Please confirm full bill payment.</b>
		</div>;

	confirmFullPayment = (billId: string) => this.setState({isLoading: true},
		() => api.instance.billApi.confirmFullBillPayment(billId)
			.then(() => {
				this.fetchData(() => {
					this.setState({isLoading: false});
					Toast.success("Full payment confirmed successfully", "Confirm full payment");
				});
			})
			.catch(err => {
				this.setState({isLoading: false});
				notifyBugsnagAndShowToast(err, "Confirm full payment");
			})
	);

	redirectToJob = () => this.props.parentJobRefNumber.map(jrn => this.props.history.push(Routes.EditJob(jrn)))

	onBillDateChange = (billDate: Moment) => {
		this.setState({billDate: option(billDate)})
	}

	onBillDueDateChange = (dueDate: Moment) => {
		this.setState({dueDate: option(dueDate)})
	}

	isUpdateButtonDisabled = () => this.state.isUpdateBillDatesLoading
		|| this.state.billDate.isEmpty
		|| (OptionUtils.isTheSameMoment(this.state.dueDate, this.props.bill.flatMap(b => b.dueDate)) &&
			OptionUtils.isTheSameMoment(this.state.billDate, this.props.bill.map(b => b.billDate)))

	onUpdateBillDates = () => {
		if (this.state.billDate.isDefined && this.props.bill.isDefined) {
			this.setState({isUpdateBillDatesLoading: true},
				() => this.props.updateBill(
					this.props.bill.get.copy({billDate: this.state.billDate.get, dueDate: this.state.dueDate}),
					() => {
						Toast.success("Bill dates updated successfully", "Update Bill dates");
						this.setState({isUpdateBillDatesLoading: false, editBillInfo: false})
					},
					e => {
						notifyBugsnagAndShowToast(e, "Update Bill dates");
						this.setState({editBillInfo: false, isUpdateBillDatesLoading: false})
					}))

		}
	}

	downloadBillPdf = () => {
		this.props.bill.forEach(b => window.open(`/api/web/accounting/bills/${b.id}/pdf`, "_blank"))
	}
}

function mapStateToProps(state: RootState, props: Props) {
	const bill = option(state.bill.billsMap[props.match.params.billId]);
	const partnerBrand = option(state.partnerBrand.partnerBrandsMap[bill.map(b => b.partnerBrandId).orNull]);
	const job = bill.flatMap(b => b.jobId).flatMap(id => getJobByIdOrRefNumber(id, state));
	const jobStatus = job.flatMap(j => getJobStatus(j.statusId, state));
	const jobStatusLabel = jobStatus.map(js => js.label);
	const lastBillPaidNotificationId = option(state.notification.notifications.filter(
		n => n.type === NotificationTypeEnum.BillPaid && (n as BillPaidNotification).data.billId ===
			props.match.params.billId)[0]).map(n => n.id)
	const parentJobRefNumber = job.map(j => j.referenceNumber)
	return {partnerBrand, bill, jobStatusLabel, lastBillPaidNotificationId, parentJobRefNumber, job};
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		fetchBill: (billId: string, onSuccess: OnSuccessData<Bill>, onFailure: OnFailure) => dispatch(
			fetchBill(billId, onSuccess, onFailure)),
		fetchJob: (jobId: string, onFailure: OnFailure) => dispatch(fetchJob(jobId, () => {}, onFailure)),
		fetchPartnerBrand: (pbId: string, onFailure: OnFailure) => dispatch(fetchPartnerBrand(pbId, onFailure)),
		updateBill: (bill: Bill, onSuccess: OnSuccess, onFailure: OnFailure) => dispatch(
			updateBill(bill, onSuccess, onFailure)
		)
	}
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(BillDetailsView))

enum BillAction {
	SEND_BILL = 'SEND_BILL',
	COPY_BILL_PAYMENT_LINK = "COPY_BILL_PAYMENT_LINK",
	EDIT_BILL_INFO = "EDIT_BILL_INFO",
	DOWNLOAD_PDF = "DOWNLOAD_PDF",
	MARK_BILL_AS_PAID = "MARK_BILL_AS_PAID"
}
